import { useState, useEffect } from 'react';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Ticket } from '../models/Ticket';
import { logError } from '../../lib/utils';

export function useTickets(projectId: string | null | undefined) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setTickets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ticketsRef = collection(db, `tickets/${projectId}/issues`);

    // Query without orderBy to avoid index requirement, we'll sort in JavaScript
    const q = query(ticketsRef);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ticketsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            lastSyncedAt: data.lastSyncedAt?.toDate() || new Date(),
            created: data.created?.toDate() || data.lastSyncedAt?.toDate() || new Date(),
          } as Ticket;
        });

        // Sort by created date descending (newest first)
        ticketsData.sort((a, b) => {
          const aTime = a.created?.getTime() || a.lastSyncedAt.getTime();
          const bTime = b.created?.getTime() || b.lastSyncedAt.getTime();
          return bTime - aTime; // Descending order
        });

        setTickets(ticketsData);
        setLoading(false);
        setError(null);
      },
      (err: unknown) => {
        logError('clients', err, { source: 'useTickets.subscription', projectId });
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  /**
   * Generate the next available ticket key for a project
   * Format: {projectKey}-{number}
   * Example: "PROJ-1", "PROJ-2", etc.
   */
  const generateNextKey = async (projectId: string, projectKey?: string): Promise<string> => {
    try {
      const ticketsRef = collection(db, `tickets/${projectId}/issues`);
      const snapshot = await getDocs(ticketsRef);

      if (snapshot.empty) {
        // No tickets exist, start at 1
        const prefix = projectKey || 'PROJ';
        return `${prefix}-1`;
      }

      // Extract all numeric values from existing keys
      const keyNumbers: number[] = [];
      let detectedPrefix = projectKey;

      snapshot.docs.forEach((doc) => {
        const ticket = doc.data() as Ticket;
        if (ticket.key) {
          // Extract prefix and number from key (e.g., "PROJ-123" -> prefix: "PROJ", number: 123)
          const match = ticket.key.match(/^([A-Z]+)-(\d+)$/i);
          if (match) {
            const [, prefix, numberStr] = match;
            if (!detectedPrefix) {
              detectedPrefix = prefix.toUpperCase();
            }
            const number = parseInt(numberStr, 10);
            if (!isNaN(number)) {
              keyNumbers.push(number);
            }
          }
        }
      });

      // Use detected prefix or fallback
      const prefix = detectedPrefix || projectKey || 'PROJ';

      // Find the maximum number and increment
      const maxNumber = keyNumbers.length > 0 ? Math.max(...keyNumbers) : 0;
      const nextNumber = maxNumber + 1;

      return `${prefix}-${nextNumber}`;
    } catch (err: unknown) {
      logError('clients', err, { source: 'useTickets.generateNextKey', projectId });
      const prefix = projectKey || 'PROJ';
      return `${prefix}-${Date.now()}`;
    }
  };

  const createTicket = async (
    projectId: string,
    ticketData: Omit<Ticket, 'id' | 'key' | 'lastSyncedAt' | 'created' | 'projectId'>,
    projectKey?: string
  ) => {
    try {
      if (!projectId) {
        throw new Error('projectId is required to create a ticket');
      }

      // Generate the next key
      const key = await generateNextKey(projectId, projectKey);

      const ticketsRef = collection(db, `tickets/${projectId}/issues`);
      const now = Timestamp.now();

      await addDoc(ticketsRef, {
        ...ticketData,
        key,
        projectId,
        lastSyncedAt: now,
        created: now,
      });
    } catch (err: unknown) {
      logError('clients', err, { source: 'useTickets.createTicket', projectId });
      throw err;
    }
  };

  const updateTicket = async (
    projectId: string,
    ticketId: string,
    updates: Partial<Omit<Ticket, 'id' | 'key' | 'projectId'>>
  ) => {
    try {
      if (!projectId) {
        throw new Error('projectId is required to update a ticket');
      }

      const ticketRef = doc(db, `tickets/${projectId}/issues`, ticketId);
      await updateDoc(ticketRef, {
        ...updates,
        lastSyncedAt: Timestamp.now(),
      });
    } catch (err: unknown) {
      logError('clients', err, { source: 'useTickets.updateTicket', projectId, ticketId });
      throw err;
    }
  };

  const deleteTicket = async (projectId: string, ticketId: string) => {
    try {
      if (!projectId) {
        throw new Error('projectId is required to delete a ticket');
      }

      const ticketRef = doc(db, `tickets/${projectId}/issues`, ticketId);
      await deleteDoc(ticketRef);
    } catch (err: unknown) {
      logError('clients', err, { source: 'useTickets.deleteTicket', projectId, ticketId });
      throw err;
    }
  };

  return {
    tickets,
    loading,
    error,
    createTicket,
    updateTicket,
    deleteTicket,
    generateNextKey,
  };
}
